import React, { useState, useEffect, useContext } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import FormControl from "@material-ui/core/FormControl";
import Select from "@material-ui/core/Select";
import MenuItem from "@material-ui/core/MenuItem";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Chip from "@material-ui/core/Chip";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import AssignmentIndIcon from "@material-ui/icons/AssignmentInd";
import RefreshIcon from "@material-ui/icons/Refresh";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainContainer from "../../components/MainContainer";
import ContactDrawer from "../../components/ContactDrawer";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles((theme) => ({
  mainContainer: {
    display: "flex",
    flexDirection: "column",
    height: "calc(100vh - 64px)",
    padding: theme.spacing(1),
    overflow: "hidden",
  },
  filterPaper: {
    padding: "12px 16px",
    marginBottom: 12,
    backgroundColor: "#f8fafc",
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 16,
  },
  kanbanBoard: {
    display: "flex",
    flex: 1,
    gap: 16,
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: 12,
    ...theme.scrollbarStyles,
  },
  kanbanColumn: {
    width: 320,
    minWidth: 320,
    maxHeight: "100%",
    backgroundColor: "#f1f5f9",
    borderRadius: 8,
    border: "1px solid #cbd5e1",
    display: "flex",
    flexDirection: "column",
  },
  columnHeader: {
    padding: "12px 16px",
    display: "flex",
    justify: "space-between",
    alignItems: "center",
    borderBottom: "2px solid #e2e8f0",
  },
  cardsContainer: {
    flex: 1,
    padding: 12,
    overflowY: "auto",
    display: "flex",
    flexDirection: "column",
    gap: 12,
    ...theme.scrollbarStyles,
  },
  contactCard: {
    padding: 12,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    border: "1px solid #e2e8f0",
    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
    cursor: "grab",
    transition: "transform 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 6px rgba(0,0,0,0.08)",
    },
    "&:active": {
      cursor: "grabbing",
    },
  },
}));

const Pipeline = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const [whatsapps, setWhatsapps] = useState([]);
  const [selectedWhatsappId, setSelectedWhatsappId] = useState("");
  const [availableStatuses, setAvailableStatuses] = useState([]);
  const [availableServices, setAvailableServices] = useState([]);
  const [selectedServiceFilter, setSelectedServiceFilter] = useState("");

  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerContact, setDrawerContact] = useState(null);

  // Load Whatsapps list
  useEffect(() => {
    const fetchWhatsapps = async () => {
      try {
        const { data } = await api.get("/whatsapp");
        setWhatsapps(data || []);
        if (data && data.length > 0) {
          setSelectedWhatsappId(data[0].id.toString());
        }
      } catch (err) {
        toastError(err);
      }
    };
    fetchWhatsapps();
  }, []);

  // Load Statuses & Services for selected Whatsapp
  useEffect(() => {
    const fetchSettings = async () => {
      if (!selectedWhatsappId) return;
      try {
        const { data } = await api.get("/settings");
        const wappId = selectedWhatsappId;
        const statSetting = data.find(s => s.key === `statuses_list_wapp_${wappId}`) || data.find(s => s.key === "statuses_list");
        if (statSetting && statSetting.value) {
          setAvailableStatuses(JSON.parse(statSetting.value));
        } else {
          setAvailableStatuses([]);
        }

        const servSetting = data.find(s => s.key === `services_list_wapp_${wappId}`) || data.find(s => s.key === "services_list");
        if (servSetting && servSetting.value) {
          setAvailableServices(JSON.parse(servSetting.value));
        } else {
          setAvailableServices([]);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchSettings();
  }, [selectedWhatsappId]);

  // Fetch Contacts for selected Whatsapp
  const loadContacts = async () => {
    if (!selectedWhatsappId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/contacts/", {
        params: {
          whatsappId: selectedWhatsappId,
          services: selectedServiceFilter,
          pageNumber: 1
        },
      });
      setContacts(data.contacts || []);
    } catch (err) {
      toastError(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    loadContacts();
  }, [selectedWhatsappId, selectedServiceFilter]);

  const handleDragStart = (e, contactId) => {
    e.dataTransfer.setData("text/plain", contactId.toString());
  };

  const handleDrop = async (e, targetStatusName) => {
    e.preventDefault();
    const contactIdStr = e.dataTransfer.getData("text/plain");
    if (!contactIdStr) return;
    const contactId = +contactIdStr;

    const targetContact = contacts.find(c => c.id === contactId);
    if (!targetContact) return;

    let currentInfo = Array.isArray(targetContact.extraInfo) ? [...targetContact.extraInfo] : [];
    const existingIdx = currentInfo.findIndex(i => i.name === "Status do Cliente");

    if (targetStatusName === "Sem Status") {
      if (existingIdx >= 0) {
        currentInfo.splice(existingIdx, 1);
      }
    } else {
      if (existingIdx >= 0) {
        currentInfo[existingIdx].value = targetStatusName;
      } else {
        currentInfo.push({ name: "Status do Cliente", value: targetStatusName });
      }
    }

    // Optimistic UI update
    setContacts(prev => prev.map(c => {
      if (c.id === contactId) {
        return { ...c, extraInfo: currentInfo };
      }
      return c;
    }));

    try {
      await api.put(`/contacts/${contactId}`, {
        name: targetContact.name,
        number: targetContact.number,
        email: targetContact.email,
        extraInfo: currentInfo
      });
      toast.success(`Status de ${targetContact.name} atualizado!`);
    } catch (err) {
      toastError(err);
      loadContacts(); // rollback on error
    }
  };

  const handleOpenChat = async (contactId, whatsappId) => {
    if (!contactId) return;
    try {
      const { data: ticket } = await api.post("/tickets", {
        contactId,
        userId: user?.id,
        status: "open",
        whatsappId: whatsappId || user?.whatsappId,
      });
      history.push(`/tickets/${ticket.id}`);
    } catch (err) {
      toastError(err);
    }
  };

  const handleOpenDrawer = (contact) => {
    setDrawerContact(contact);
    setDrawerOpen(true);
  };

  // Build Kanban columns: availableStatuses
  const allColumns = availableStatuses;

  return (
    <MainContainer className={classes.mainContainer}>
      <MainHeader>
        <Title>📊 Pipeline de Vendas (Kanban)</Title>
        <IconButton title="Atualizar Kanban" onClick={loadContacts}>
          <RefreshIcon />
        </IconButton>
      </MainHeader>

      <Paper variant="outlined" className={classes.filterPaper}>
        {/* SELETOR DE MARCA / CONEXÃO */}
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Typography style={{ fontWeight: "bold", fontSize: "0.9rem", color: "#0f172a" }}>
            📱 Marca / Conexão:
          </Typography>
          <FormControl variant="outlined" size="small" style={{ minWidth: 240, backgroundColor: "#ffffff" }}>
            <Select
              value={selectedWhatsappId}
              onChange={(e) => setSelectedWhatsappId(e.target.value)}
              style={{ fontWeight: "bold", color: "#0284c7" }}
            >
              {whatsapps.map(w => (
                <MenuItem key={w.id} value={w.id.toString()}>
                  📱 {w.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>

        {/* FILTRO SECUNDÁRIO DE SERVIÇO */}
        {availableServices.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Typography style={{ fontWeight: "bold", fontSize: "0.9rem", color: "#0f172a" }}>
              🩺 Filtrar por Serviço:
            </Typography>
            <FormControl variant="outlined" size="small" style={{ minWidth: 200, backgroundColor: "#ffffff" }}>
              <Select
                value={selectedServiceFilter}
                onChange={(e) => setSelectedServiceFilter(e.target.value)}
                style={{ fontSize: "0.85rem" }}
              >
                <MenuItem value="">🌐 Todos os Serviços</MenuItem>
                {availableServices.map(s => (
                  <MenuItem key={s.id || s.name} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
      </Paper>

      {/* QUADRO KANBAN */}
      <div className={classes.kanbanBoard}>
        {allColumns.map((col) => {
          const colName = col.name;
          const colColor = col.color || "#64748b";

          // Filter contacts for this column
          const colContacts = contacts.filter(contact => {
            const currentStat = contact.extraInfo?.find(i => i.name === "Status do Cliente")?.value;
            if (colName === "Sem Status") {
              return !currentStat;
            }
            return currentStat === colName;
          });

          return (
            <div
              key={col.id || colName}
              className={classes.kanbanColumn}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => handleDrop(e, colName)}
            >
              <div className={classes.columnHeader} style={{ backgroundColor: "#ffffff", borderRadius: "8px 8px 0 0" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ width: 12, height: 12, borderRadius: "50%", backgroundColor: colColor }} />
                  <Typography style={{ fontWeight: "bold", fontSize: "0.95rem", color: "#0f172a" }}>
                    {colName}
                  </Typography>
                </div>
                <span style={{ fontSize: "0.75rem", backgroundColor: "#e2e8f0", color: "#334155", borderRadius: 12, padding: "2px 8px", fontWeight: "bold" }}>
                  {colContacts.length}
                </span>
              </div>

              <div className={classes.cardsContainer}>
                {colContacts.map((contact) => {
                  const servicesList = contact.extraInfo?.filter(i => i.name === "Serviço de Interesse") || [];
                  return (
                    <Paper
                      key={contact.id}
                      className={classes.contactCard}
                      draggable
                      onDragStart={(e) => handleDragStart(e, contact.id)}
                    >
                      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                        <Avatar src={contact.profilePicUrl} style={{ width: 36, height: 36 }} />
                        <div style={{ flex: 1, overflow: "hidden" }}>
                          <Typography style={{ fontWeight: "bold", fontSize: "0.9rem", color: "#0f172a", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" }}>
                            {contact.name}
                          </Typography>
                          <Typography style={{ fontSize: "0.75rem", color: "#64748b", fontFamily: "monospace" }}>
                            {contact.number}
                          </Typography>
                        </div>
                      </div>

                      {/* SERVIÇOS BADGES */}
                      {servicesList.length > 0 && (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginBottom: 10 }}>
                          {servicesList.map((serv, idx) => (
                            <span key={idx} style={{ fontSize: "0.68rem", backgroundColor: "#e0f2fe", color: "#0369a1", borderRadius: 4, padding: "2px 6px", fontWeight: "bold" }}>
                              {serv.value}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* ACTION BUTTONS */}
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 6, borderTop: "1px solid #f1f5f9", paddingTop: 6 }}>
                        <IconButton
                          size="small"
                          title="Ficha Completa & Timeline"
                          onClick={() => handleOpenDrawer(contact)}
                          style={{ backgroundColor: "#e0f2fe", color: "#0284c7" }}
                        >
                          <AssignmentIndIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          title="Abrir Chat no WhatsApp"
                          onClick={() => handleOpenChat(contact.id, contact.whatsappId)}
                          style={{ backgroundColor: "#dcfce7", color: "#16a34a" }}
                        >
                          <WhatsAppIcon fontSize="small" />
                        </IconButton>
                      </div>
                    </Paper>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {drawerContact && (
        <ContactDrawer
          open={drawerOpen}
          handleDrawerClose={() => setDrawerOpen(false)}
          contact={drawerContact}
          loading={false}
        />
      )}
    </MainContainer>
  );
};

export default Pipeline;
